import React from 'react';
import { familyAccountsQuery } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { useAuthManager } from '@funtech-int/yango-shared';

import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import { getAvatarsImageUrl } from '@/shared/lib/images';

import { AddToFamilyButton } from '../components/AddToFamilyButton/AddToFamilyButton';
import { ProfileFamilyItem } from '../components/ProfileFamilyItem/ProfileFamilyItem';

const MAX_FAMILY_PROFILES = 7;
const PROFILE_BIG_AVATAR_URL_SIZE = '300x300';

export interface ProfileListConfig {
    profileOttId: number;
    profileImage: string;
    profileDisplayName: string;
    profileHasSubscription: boolean;
    profileAgeRestriction?: string;

    isProfilesPageAutoShowed: boolean;

    profileViewVariant?: 'vertical' | 'horizontal';
    onChooseParentAccount: VoidFunction;
}

export const useProfileList = ({
    profileImage,
    profileOttId,
    profileDisplayName,
    profileHasSubscription,
    profileAgeRestriction,
    isProfilesPageAutoShowed,
    profileViewVariant = 'vertical',
    onChooseParentAccount,
}: ProfileListConfig) => {
    const authManager = useAuthManager();

    const { data } = useGqlQuery(familyAccountsQuery, {});

    const avatarSize = profileViewVariant === 'vertical' ? 132 : 124;

    const profileIsAdmin = Boolean(data?.familyAdmin);

    const familyProfilesItems =
        data?.members.map((familyProfile) => {
            const {
                ottId,
                puid,
                displayName,
                avatarsUrl,
                isAuthorized,
                passportLogin,
                passportEmail,
                isAdmin,
                hasSubscription,
            } = familyProfile;

            const loginRetPath = `${window.location.origin}/`;

            const loginLink = isAuthorized
                ? authManager.getChangeMultiAccountUrl(String(puid), new URL(loginRetPath))
                : authManager.getAuthAccountUrl(
                      passportLogin || passportEmail || '',
                      new URL(window.location.href)
                  );

            const item = (
                <ProfileFamilyItem
                    key={String(ottId)}
                    url={loginLink?.toString()}
                    text={displayName ?? ''}
                    avatarSize={avatarSize}
                    avatarUrl={
                        getAvatarsImageUrl(
                            avatarsUrl || '',
                            PROFILE_BIG_AVATAR_URL_SIZE || 'orig'
                        ) ?? ''
                    }
                    isAdmin={isAdmin}
                    isAuthorized={isAuthorized}
                    ageRestrictionGroup={undefined}
                    isMasterProfile
                    id={ottId ?? puid}
                    isFamilyAccount
                    isProfilesPageAutoShowed={isProfilesPageAutoShowed}
                    viewVariant={profileViewVariant}
                    hasSubscription={hasSubscription}
                />
            );

            return item;
        }) || [];

    const profileItems = [
        <ProfileFamilyItem
            key={profileOttId}
            text={profileDisplayName}
            avatarUrl={profileImage}
            avatarSize={avatarSize}
            onClick={onChooseParentAccount}
            isMasterProfile
            id={profileOttId}
            isAdmin={profileIsAdmin}
            isProfilesPageAutoShowed={isProfilesPageAutoShowed}
            ageRestrictionGroup={profileAgeRestriction}
            isAuthorized
            viewVariant={profileViewVariant}
            hasSubscription={profileHasSubscription}
        />,
        ...familyProfilesItems,
    ];

    if (familyProfilesItems.length < MAX_FAMILY_PROFILES) {
        profileItems.push(
            <AddToFamilyButton isAdmin={profileIsAdmin} viewVariant={profileViewVariant} />
        );
    }

    return profileItems;
};
